import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet, RouterLink } from '@angular/router';
import { MatSidenav, MatSidenavContainer } from '@angular/material/sidenav';
import { EMPTY, Observable, Subject } from 'rxjs';
import {
  configurationSelectors,
  settingsSelectors,
  State,
  User,
  UserRole,
  UserType,
} from '@wam/shared';
import { select, Store } from '@ngrx/store';
import { authenticationSelectors, getOrgCode } from '@wam/authentication';
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  share,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { TabDisplayAggregatorService } from '@app/shared/tab-display-aggregator.service';
import { AuthenticationGuard } from '@app/authentication/authentication.guard';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { MessagesAndNotificationsTabDisplayService } from '@app/messages-and-notifications/messages-and-notifications-tab-display.service';
import { SidenavService } from '@app/shared/sidenav.service';
import { isNil } from 'lodash-es';
import { Tabs } from '@app/shared/tabs.enum';
import { Icon, IconComponent, BtnComponent } from '@wam/components';
import { LayoutService } from '@app/layouts/layout.service';
import { Location, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { IconBadgeComponent } from '../../shared/component-library/icon-badge/icon-badge.component';
import { CdkPortalOutlet } from '@angular/cdk/portal';
import { NavItemComponent } from '../../shared/component-library/nav-item/nav-item.component';
import { HelpWidgetComponent } from '../../shared/help-widget/help-widget.component';

@Component({
  selector: 'default-layout',
  templateUrl: './default-layout.component.html',
  styleUrls: ['./default-layout.component.scss'],
  host: { '(click)': 'onClick()' },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatSidenavContainer,
    MatSidenav,
    NgTemplateOutlet,
    IconComponent,
    RouterLink,
    IconBadgeComponent,
    BtnComponent,
    CdkPortalOutlet,
    NavItemComponent,
    RouterOutlet,
    HelpWidgetComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class DefaultLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  private store = inject<Store<State>>(Store);
  private tabDisplayAggregator = inject(TabDisplayAggregatorService);
  private authenticationGuard = inject(AuthenticationGuard);
  private translate = inject(TranslateService);
  private messagesAndNotificationsTabDisplayService = inject(
    MessagesAndNotificationsTabDisplayService,
  );
  sidenavService = inject(SidenavService);
  private scrollStrategyOptions = inject(ScrollStrategyOptions);
  private layoutService = inject(LayoutService);
  private location = inject(Location);
  private titleService = inject(Title);
  private host: ElementRef<HTMLElement> = inject(ElementRef);

  @ViewChild(RouterOutlet) outlet: RouterOutlet;
  @ViewChild('mobileMenu') mobileMenu: MatSidenav;
  @ViewChild('drawer') private drawer: MatSidenav;

  private destroyed$ = new Subject<void>();
  selected: Tabs = Tabs.LOGIN;
  private displayedTabs: string[] = [];
  private firstTab: Tabs;

  readonly HIDDEN_EMAIL_DOMAIN_NAME = '@privaterelay';

  loggedInUser: User;
  logo: string;
  mobileLogo: string;
  showLoginMenu = false;

  Tabs = Tabs;
  Icon = Icon;
  href = window.location.href;

  tabPosition$ = this.store.select(
    configurationSelectors.getUserConfiguration<'top' | 'left'>('tabPosition'),
  );
  isSchoolOrDistrictAdmin$: Observable<boolean> = this.store.pipe(
    select(authenticationSelectors.getUserRoles),
    map(
      (roles: UserRole[]) =>
        roles?.includes(UserRole.SCHOOL_ADMIN) || roles?.includes(UserRole.DISTRICT_ADMIN),
    ),
  );
  isPlaylistAdmin$ = this.store.pipe(
    select(authenticationSelectors.getCurrentUser),
    map(
      (user: User) =>
        user.type === UserType.ADMIN &&
        (user.roles.includes(UserRole.INTERNAL_ADMIN) ||
          user.roles.includes(UserRole.CURRICULUM_ADMIN) ||
          user.roles.includes(UserRole.ACCOUNT_ADMIN)),
    ),
  );
  showBell$: Observable<boolean> = this.messagesAndNotificationsTabDisplayService.shouldShow();
  bellItemCount$: Observable<number> =
    this.messagesAndNotificationsTabDisplayService.notificationCount();
  orgCode$ = this.store.select(authenticationSelectors.getOrgCode);

  blockScroll: ScrollStrategy = this.scrollStrategyOptions.block();
  tabTitles: Map<Tabs, Observable<string>> = new Map([
    [Tabs.TEACHER_HOME, this.translate.stream('Home')],
    [Tabs.ADMIN_HOME, this.translate.stream('Home')],
    [Tabs.ACCOUNTS, this.translate.stream('Accounts')],
    [Tabs.CONFIGURATION, this.translate.stream('Configuration')],
    [Tabs.TEACHER_DASHBOARDS, this.translate.stream('Dashboards')],
    [Tabs.REPORTS, this.translate.stream('Reports')],
    [Tabs.ORGANIZATIONS, this.translate.stream('Organizations')],
    [
      Tabs.TEACHER_USERS,
      this.isSchoolOrDistrictAdmin$.pipe(
        mergeMap((isSchoolOrDistrictAdmin) =>
          isSchoolOrDistrictAdmin
            ? this.translate.stream('Staff & Students')
            : this.translate.stream('Students'),
        ),
      ),
    ],
    [Tabs.GOALS, this.translate.stream('Courses & Goals')],
    [Tabs.ASSESSMENTS, this.translate.stream('Assessment')],
    [Tabs.ACTIVITIES, this.translate.stream('Resources & Activities')],
    [Tabs.NOTIFICATIONS_MANAGER, this.translate.stream('Notifications Manager')],
    [Tabs.MESSAGES_AND_NOTIFICATIONS, this.translate.stream('Messages & Notifications')],
    [Tabs.FAMILIES, this.translate.stream('Families')],
    [Tabs.PROFILE, this.translate.stream('Profile')],
  ]);

  get isPrintingRoute(): boolean {
    return this.router.url.includes('-printing');
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.subscribeToDisplayedTabs();
    this.subscribeToFirstTab();
    this.subscribeToRouterEvents();
    this.setLoggedInUser();
    this.getOrgCode();
    this.initializeLogo();
    this.subscribeToSelectedTab();
  }

  logOut(): void {
    this.router.navigateByUrl('/logout');
  }

  isSelected(tab: string): boolean {
    return this.selected === Tabs[tab];
  }

  isDisplayed(tab: string): boolean {
    return this.displayedTabs.includes(tab);
  }

  notificationCount(tab: string): Observable<number> {
    return this.tabDisplayAggregator.getNotificationCountForTab(tab);
  }

  private getOrgCode(): void {
    this.store
      .pipe(
        select(authenticationSelectors.getUserRoles),
        tap((roles: UserRole[]) => {
          if (
            roles?.includes(UserRole.CLASS_ADMIN) ||
            roles?.includes(UserRole.SCHOOL_ADMIN) ||
            roles?.includes(UserRole.DISTRICT_ADMIN)
          ) {
            this.store.dispatch(getOrgCode());
          }
        }),
        take(1),
      )
      .subscribe();
  }

  setLoggedInUser(): void {
    this.store
      .pipe(
        select(authenticationSelectors.getCurrentUser),
        filter((user) => !isNil(user)),
        takeUntil(this.destroyed$),
      )
      .subscribe((loggedInUser: User) => (this.loggedInUser = loggedInUser));
  }

  private subscribeToDisplayedTabs(): void {
    this.tabDisplayAggregator
      .getDisplayedTabs()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((displayedTabs) => {
        const tabCountBecameZero = this.displayedTabs.length !== 0 && displayedTabs.length === 0;
        if (tabCountBecameZero) {
          this.router.navigate(['/']);
        }
        this.displayedTabs = displayedTabs;
      });
  }

  subscribeToFirstTab(): void {
    this.tabDisplayAggregator
      .getFirstTab()
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((firstTabName) => {
        this.firstTab = Tabs[firstTabName];
        if (this.location.path() === '') {
          this.selected = Tabs[firstTabName];
          this.navigateToTab(this.firstTab);
        }
      });
  }

  navigateToTab(tab: Tabs): Promise<boolean> {
    const getTabRoute = (t: Tabs) => this.router.config.find(({ data }) => data?.['tab'] === t);
    const route = getTabRoute(tab);
    if (route?.path && route?.path !== '**') {
      return this.router.navigateByUrl(route.path);
    } else {
      return Promise.resolve(false);
    }
  }

  private subscribeToRouterEvents(): void {
    const routerEvents$ = this.router.events.pipe(share());

    const navigationEnd$ = routerEvents$.pipe(
      filter((event) => event instanceof NavigationEnd),
      takeUntil(this.destroyed$),
    );

    navigationEnd$
      .pipe(
        mergeMap(() => this.authenticationGuard.canActivate()),
        filter((loggedIn) => loggedIn),
        takeUntil(this.destroyed$),
        mergeMap(() => {
          this.mobileMenu?.close();
          this.drawer?.close();
          this.blockScroll?.disable();
          if (this.router.url === '/') {
            return this.navigateToTab(this.firstTab);
          }

          return EMPTY;
        }),
      )
      .subscribe();
  }

  private initializeLogo(): void {
    this.store
      .pipe(
        select(settingsSelectors.getUserType),
        map((userType) => {
          switch (userType) {
            default:
              return [
                '../assets/logos/reading-academy.png',
                '../assets/logos/reading-academy-mobile.png',
              ];
          }
        }),
        take(1),
      )
      .subscribe(([logo, mobileLogo]) => {
        this.logo = logo;
        this.mobileLogo = mobileLogo;
      });
  }

  ngAfterViewInit(): void {
    this.sidenavService.setSidenav(this.drawer);
  }

  subscribeToSelectedTab() {
    this.layoutService.selectedTab$.pipe(takeUntil(this.destroyed$)).subscribe((selectedTab) => {
      this.selected = selectedTab;

      this.tabTitles
        .get(selectedTab)
        ?.pipe(take(1))
        .subscribe((title) => {
          this.titleService.setTitle(`${title} – Waterford Early Learning`);
        });
    });
  }

  disableScroll(): void {
    this.blockScroll.enable();
  }

  enableScroll(): void {
    if (!this.mobileMenu.opened && !this.drawer.opened) {
      this.blockScroll.disable();
    }
  }

  get emailIsHidden(): boolean {
    return this.loggedInUser?.email?.includes(this.HIDDEN_EMAIL_DOMAIN_NAME);
  }

  toggleShowLoginMenu() {
    this.showLoginMenu = !this.showLoginMenu;
  }

  onClick() {
    this.showLoginMenu = false;
  }

  skipToMain(event: MouseEvent) {
    event.preventDefault();

    const main = this.host.nativeElement.querySelector<HTMLElement>('#main-content');
    if (!main) return;

    // Move focus first (screen-reader + keyboard consistent)
    main.focus({ preventScroll: true });

    // Then scroll it into view (smooth optional)
    main.scrollIntoView({ block: 'start' });
  }
}
